import { useState, useCallback, useEffect } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import AddNewInstructionsPage from "../../components/AddNewInstructionsPage";
import NavigationPanelLawyer from "../../components/NavigationPanelLawyer";
import PortalPopup from "../../components/PortalPopup";
import ExportModalImage from "../../components/ExportModalImage";
import LogoutConfirmation from "../../components/LogoutConfirmation";
import InstructionCard from "../../components/InstructionCard";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import { Legend } from "chart.js";
import axios from "axios";
import Footer from "../../components/Footer";
import CTAs from "../../components/CTAs";
import PoweredByAITag from "../../components/PoweredByAITag";

const CDI_1 = () => {
  const navigate = useNavigate();
  const [isAddNewInstructionsPage1Open, setAddNewInstructionsPage1Open] =
    useState(false);
  const [isExportModalImageOpen, setExportModalImageOpen] = useState(false);
  const [isLogoutConfirmationOpen, setLogoutConfirmationOpen] = useState(false);

  const onClickHandlingDefences = useCallback(() => {
    navigate("/cdi2");
  }, [navigate]);

  const openAddNewInstructionsPage1 = useCallback(() => {
    setAddNewInstructionsPage1Open(true);
  }, []);

  const closeAddNewInstructionsPage1 = useCallback(() => {
    setAddNewInstructionsPage1Open(false);
  }, []);

  const openExportModalImage = useCallback(() => {
    setExportModalImageOpen(true);
  }, []);

  const closeExportModalImage = useCallback(() => {
    setExportModalImageOpen(false);
  }, []);

  const onCTAsContainerClick = useCallback(() => {
    navigate("/cdi3");
  }, [navigate]);

  const onDebtCollectionNavContainer3Click = useCallback(() => {
    // Add your code here
  }, []);

  const closeLogoutConfirmation = useCallback(() => {
    setLogoutConfirmationOpen(false);
  }, []);

  const navItems = [
    {
      text: "Dashboard",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/dashboard",
    },
    {
      text: "Legal proceedings & billing",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/legal_1",
    },
    {
      text: "Pre-LBA checks",
      bgColor: "bg-neutrals-neutral-50",
      textColor: "text-neutrals-neutral-300",
      hoverBgColor: "bg-neutrals-neutral-50",
      hoverTextColor: "text-primary-primary-500",
      path: "/prelbacheck",
    },
    {
      text: "Client-debtor interaction",
      bgColor: "bg-primary-primary-50",
      textColor: "text-neutrals-neutral-900",
      hoverBgColor: "bg-primary-primary-50",
      hoverTextColor: "text-neutrals-neutral-900",
      path: "",
    },
  ];

  const [useAPI, setUseAPI] = useState(false);
  const USE_API = false; // Set to true to fetch from API

  const [data, setData] = useState([
    { name: "A", value: 30 },
    { name: "B", value: 50 },
    { name: "C", value: 20 },
  ]);

  // Function to fetch data from API
  const fetchData = async () => {
    if (!USE_API) return; // Skip API fetch if using dummy data

    try {
      const response = await axios.get("https://api.example.com/realtime-data"); // Replace with actual API URL
      setData(response.data); // Ensure API response matches [{ name: "A", value: 30 }, ...] format
    } catch (error) {
      console.error("Error fetching data:", error);
    }
  };

  useEffect(() => {
    if (USE_API) {
      fetchData(); // Fetch initial data
      const interval = setInterval(() => {
        fetchData();
      }, 3000);
      return () => clearInterval(interval); // Cleanup on unmount
    } else {
      // If using dummy data, update randomly every 3 seconds (simulated real-time updates)
      const interval = setInterval(() => {
        setData([
          { name: "A", value: Math.floor(Math.random() * 60) },
          { name: "B", value: Math.floor(Math.random() * 60) },
          { name: "C", value: Math.floor(Math.random() * 60) },
        ]);
      }, 3000);
      return () => clearInterval(interval);
    }
  }, []);

  // Pie Chart
  const COLORS = ["#335869", "#487C94", "#EDF2F4"]; // Active segment colors
  const BG_COLOR = "#EDF2F4"; // Background color for empty space

  const [instructionData, setInstructionData] = useState([
    { name: "High", value: 10 },
    { name: "Medium", value: 20 },
    { name: "Low", value: 30 },
  ]);

  const [normalizedData, setNormalizedData] = useState([]);

  // Normalize raw data so total value is 100
  useEffect(() => {
    const total = instructionData.reduce((sum, item) => sum + item.value, 0);
    if (total === 0) return;

    const normalized = instructionData.map((item) => ({
      name: item.name,
      value: parseFloat(((item.value / total) * 100).toFixed(2)),
    }));

    setNormalizedData(normalized);
  }, [instructionData]);

  // Placeholder for fetching real-time data from API
  useEffect(() => {
    fetch("https://your-api-endpoint.com/instruction-data")
      .then((res) => res.json())
      .then((data) => {
        setInstructionData(data);
      })
      .catch((err) => console.error("API fetch error:", err));
  }, []);

  return (
    <>
      <div className="w-full relative bg-neutrals-neutral-50 min-h-screen overflow-visible text-left text-base text-neutrals-neutral-900 font-open-sans-ff-body-strong">
        {/* Navigation Panel */}
        <NavigationPanelLawyer navItems={navItems} />

        {/* Main */}
        <div className="w-full absolute top-[136px] flex flex-col items-start justify-start gap-6 text-neutrals-neutral-900 px-4 md:px-4 lg:px-24 box-border">
          <div className="w-full flex flex-col items-start justify-start gap-1">
            <b className="self-stretch relative text-9xl">
              Client-debtor interaction
            </b>
            <div className="self-stretch relative text-base text-neutrals-neutral-300">{`Get all your information for client & debtor over here.`}</div>
          </div>
          <div className="self-stretch flex flex-col items-start justify-start gap-8 text-lg">
            {/* Tabs and blocks */}
            <div className="w-full flex flex-col items-start justify-start gap-6 text-base text-primary-primary-500">
              {/* Tabs */}
              <div className="flex flex-row items-start justify-start gap-6">
                <div className="bg-bg-primary border-primary-primary-500 border-solid border-b-[2px] box-border h-[41px] flex flex-row items-center justify-center pt-0 px-1 pb-3">
                  <div className="flex flex-row items-center justify-center">
                    <div className="relative font-semibold">
                      Client instructions
                    </div>
                  </div>
                </div>
                <div
                  className="bg-bg-primary h-[41px] flex flex-row items-center justify-center pt-0 px-1 pb-3 box-border cursor-pointer text-neutrals-neutral-300"
                  onClick={onClickHandlingDefences}
                >
                  <div className="flex flex-row items-center justify-center">
                    <div className="relative font-semibold">
                      Handling defences
                    </div>
                  </div>
                </div>
              </div>

              {/* Blocks */}
              <div className="self-stretch grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-start justify-start gap-6 text-neutrals-neutral-300">
                {/* Block 1 */}
                <div className="w-full shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start p-6 gap-2.5">
                  <div className="self-stretch flex flex-row items-center justify-between gap-0">
                    <div className="relative">Total instructions</div>
                    <img
                      className="w-5 relative h-5 overflow-hidden shrink-0"
                      alt=""
                      src="assets/more-vert.svg"
                    />
                  </div>
                  <b className="relative text-13xl text-neutrals-neutral-900">
                    15
                  </b>
                </div>
                {/* Block 2 */}
                <div className="w-full shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start p-6 gap-2.5">
                  <div className="self-stretch flex flex-row items-center justify-between gap-0">
                    <div className="relative">Pending instructions</div>
                    <img
                      className="w-5 relative h-5 overflow-hidden shrink-0"
                      alt=""
                      src="assets/more-vert.svg"
                    />
                  </div>
                  <b className="relative text-13xl text-neutrals-neutral-900">
                    07
                  </b>
                </div>
                {/* Block 3 */}
                <div className="w-full shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-col items-start justify-start p-6 gap-2.5">
                  <div className="self-stretch flex flex-row items-center justify-between gap-0">
                    <div className="relative">Completed instructions</div>
                    <img
                      className="w-5 relative h-5 overflow-hidden shrink-0"
                      alt=""
                      src="assets/more-vert.svg"
                    />
                  </div>
                  <b className="relative text-13xl text-neutrals-neutral-900">
                    08
                  </b>
                </div>
              </div>
            </div>
            <div className="self-stretch flex flex-col items-start justify-start gap-6">
              <div className="self-stretch border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-row items-start justify-between pt-0 px-0 pb-6 gap-4">
                <div className="flex flex-col items-start justify-start gap-1.5">
                  <div className="relative font-semibold">Instruction list</div>
                  <div className="relative text-base text-neutrals-neutral-300">
                    List of all the pending and completed instructions.
                  </div>
                </div>
                <CTAs
                  type="Secondarybtn"
                  text="New instruction"
                  borderColor="border-neutrals-neutral-100"
                  icon={
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      width="20"
                      height="20"
                      viewBox="0 0 24 24"
                      fill="none"
                    >
                      <path
                        d="M6 12h12M12 18V6"
                        stroke="#2d404e"
                        stroke-width="1.5"
                        stroke-linecap="round"
                        stroke-linejoin="round"
                      ></path>
                    </svg>
                  }
                  onClick={openAddNewInstructionsPage1}
                />
              </div>
              <div className="self-stretch flex flex-col items-start justify-start gap-6 text-sm text-neutrals-neutral-300">
                <div className="self-stretch flex flex-row items-start justify-between gap-4">
                  <div className="w-[294px] shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-bg-primary border-neutrals-neutral-100 border-solid border-[1px] box-border flex flex-row items-center justify-start py-2.5 px-3.5">
                    <div className="flex flex-row items-center justify-start gap-2.5">
                      <img
                        className="w-5 relative h-5"
                        alt=""
                        src="assets/search.svg"
                      />
                      <div className="relative">Search</div>
                    </div>
                  </div>
                  <div className="flex flex-row items-start justify-start gap-2.5 text-neutrals-neutral-900">
                    <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-bg-primary border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-center py-2.5 px-3.5">
                      <div className="flex flex-row items-center justify-center">
                        <div className="relative font-semibold">
                          Sort by: Date
                        </div>
                      </div>
                    </div>
                    <div className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-bg-primary border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-center py-2.5 px-3.5">
                      <div className="flex flex-row items-center justify-center gap-1.5">
                        <img
                          className="w-5 relative h-5"
                          alt=""
                          src="assets/filter.svg"
                        />
                        <div className="relative font-semibold">Filter</div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="self-stretch grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 items-start justify-start flex-wrap content-start gap-6 text-base text-neutrals-neutral-900">
                  {/* Card 1 */}
                  <InstructionCard
                    title="Review payment history"
                    description="Review debtor's recent payment history and provide recommendations."
                    date="05/02/25"
                    status="Pending"
                    statusBg="bg-semantics-moderate-moderate-50"
                    statusText="text-semantics-moderate-moderate-500"
                    label="High"
                    labelBg="bg-white"
                    labelText="text-semantics-high-high-500"
                    navigateTo="/cdi3"
                  />
                  {/* Card 2 */}
                  <InstructionCard
                    title="Review payment history"
                    description="Draft a follow-up email to the debtor regarding the outstanding payment."
                    date="05/02/25"
                    status="Completed"
                    statusBg="bg-semantics-secure-blue-50"
                    statusText="text-semantics-secure-blue-500"
                    label="Medium"
                    labelBg="bg-white"
                    labelText="text-semantics-moderate-moderate-500"
                    navigateTo="/cdi3"
                  />
                  {/* Card 3 */}
                  <InstructionCard
                    title="Review payment history"
                    description="Draft a follow-up email to the debtor regarding the outstanding payment."
                    date="05/02/25"
                    status="In progress"
                    statusBg="bg-semantics-low-low-50"
                    statusText="text-semantics-low-low-500"
                    label="Medium"
                    labelBg="bg-white"
                    labelText="text-semantics-moderate-moderate-500"
                    navigateTo="/cdi3"
                  />
                  {/* Card 4 */}
                  <InstructionCard
                    title="Review payment history"
                    description="Draft a follow-up email to the debtor regarding the outstanding payment."
                    date="05/02/25"
                    status="Completed"
                    statusBg="bg-semantics-secure-blue-50"
                    statusText="text-semantics-secure-blue-500"
                    label="Medium"
                    labelBg="bg-white"
                    labelText="text-semantics-moderate-moderate-500"
                    navigateTo="/cdi3"
                  />
                  {/* Card 5 */}
                  <InstructionCard
                    title="Review payment history"
                    description="Draft a follow-up email to the debtor regarding the outstanding payment."
                    date="05/02/25"
                    status="Completed"
                    statusBg="bg-semantics-secure-blue-50"
                    statusText="text-semantics-secure-blue-500"
                    label="Medium"
                    labelBg="bg-white"
                    labelText="text-semantics-moderate-moderate-500"
                    navigateTo="/cdi3"
                  />
                  {/* Card 6 */}
                  <InstructionCard
                    title="Review payment history"
                    description="Draft a follow-up email to the debtor regarding the outstanding payment."
                    date="05/02/25"
                    status="Completed"
                    statusBg="bg-semantics-secure-blue-50"
                    statusText="text-semantics-secure-blue-500"
                    label="Medium"
                    labelBg="bg-white"
                    labelText="text-semantics-moderate-moderate-500"
                    navigateTo="/cdi3"
                  />
                </div>
              </div>
            </div>
            <div className="self-stretch flex flex-col items-start justify-start gap-6">
              <div className="self-stretch border-neutrals-neutral-100 border-solid border-b-[1px] flex flex-row items-start justify-between pt-0 px-0 pb-6 gap-4">
                <div className="flex flex-col items-start justify-start gap-1.5">
                  <div className="relative font-semibold">
                    Data Visualisation
                  </div>
                  <div className="relative text-base text-neutrals-neutral-300">
                    Get useful insights.
                  </div>
                </div>
                <PoweredByAITag />
              </div>
              <div className="self-stretch flex flex-row items-start justify-start gap-6">
                {/* Pie chart */}
                <div className="self-stretch shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] box-border h-auto flex flex-col items-start justify-start p-6 gap-6">
                  <div className="w-[404px] border-neutrals-neutral-100 border-solid border-b-[1px] box-border flex flex-row items-center justify-between pt-0 px-0 pb-5 gap-0">
                    <div className="relative font-semibold">
                      Distribution of instruction
                    </div>
                    <img
                      className="w-5 relative h-5 overflow-hidden shrink-0"
                      alt=""
                      src="assets/more-vert.svg"
                    />
                  </div>
                  {/* Legend */}
                  <div className="w-full flex flex-row items-start justify-between gap-6 text-sm">
                    <div className="w-80 h-80">
                      <ResponsiveContainer width="100%" height="100%">
                        <PieChart>
                          <Pie
                            data={normalizedData}
                            dataKey="value"
                            nameKey="name"
                            cx="50%"
                            cy="50%"
                            outerRadius={150}
                            fill="#8884d8"
                          >
                            {normalizedData.map((entry, index) => (
                              <Cell
                                key={`cell-${index}`}
                                fill={COLORS[index % COLORS.length]}
                              />
                            ))}
                          </Pie>
                          <Tooltip />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="flex flex-col items-start justify-start gap-1.5">
                      <div className="flex flex-row items-center justify-start gap-1.5">
                        <div className="w-2 relative rounded-[50%] bg-primary-primary-700 h-2" />
                        <div className="relative">High</div>
                      </div>
                      <div className="flex flex-row items-center justify-start gap-1.5">
                        <div className="w-2 relative rounded-[50%] bg-primary-primary-500 h-2" />
                        <div className="relative">Medium</div>
                      </div>
                      <div className="flex flex-row items-center justify-start gap-1.5">
                        <div className="w-2 relative rounded-[50%] bg-primary-primary-50 h-2" />
                        <div className="relative">Low</div>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Bar chart */}
                <div className="w-full shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-white border-neutrals-neutral-100 border-solid border-[1px] flex flex-col items-start justify-start p-6 text-sm">
                  <div className="w-full flex flex-col items-start justify-start gap-6">
                    <div className="w-full flex flex-row items-center justify-between gap-0 text-lg">
                      <div className="relative font-semibold">
                        Distribution of instructions by status
                      </div>
                      <div
                        className="shadow-[0px_1px_2px_rgba(0,_0,_0,_0.05)] rounded-3xs bg-bg-primary border-neutrals-neutral-100 border-solid border-[1px] flex flex-row items-center justify-center py-2.5 px-3.5 cursor-pointer text-sm"
                        onClick={openExportModalImage}
                      >
                        <div className="flex flex-row items-center justify-center gap-1.5">
                          <img
                            className="w-5 relative h-5"
                            alt=""
                            src="assets/export.svg"
                          />
                          <div className="relative font-semibold">Export</div>
                        </div>
                      </div>
                    </div>
                    {/* Legend */}
                    <div className="flex flex-row items-start justify-start gap-2.5 text-neutrals-neutral-300">
                      <div className="flex flex-row items-center justify-start gap-1.5">
                        <div className="w-2 relative rounded-[50%] bg-primary-primary-50 h-2" />
                        <div className="relative">Pending</div>
                      </div>
                      <div className="flex flex-row items-center justify-start gap-1.5">
                        <div className="w-2 relative rounded-[50%] bg-primary-primary-500 h-2" />
                        <div className="relative">Completed</div>
                      </div>
                      <div className="flex flex-row items-center justify-start gap-1.5">
                        <div className="w-2 relative rounded-[50%] bg-primary-primary-700 h-2" />
                        <div className="relative">In progress</div>
                      </div>
                    </div>
                    <div className="w-full flex flex-row items-start justify-start gap-2.5 text-center">
                      <ResponsiveContainer width="100%" height={300}>
                        <BarChart
                          data={data}
                          margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" />
                          <XAxis dataKey="name" />
                          <YAxis />
                          <Tooltip />
                          <Legend />
                          <Bar dataKey="value" fill="#487C94" />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {isAddNewInstructionsPage1Open && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeAddNewInstructionsPage1}
        >
          <AddNewInstructionsPage onClose={closeAddNewInstructionsPage1} />
        </PortalPopup>
      )}
      {isExportModalImageOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeExportModalImage}
        >
          <ExportModalImage onClose={closeExportModalImage} />
        </PortalPopup>
      )}
      {isLogoutConfirmationOpen && (
        <PortalPopup
          overlayColor="rgba(113, 113, 113, 0.3)"
          placement="Centered"
          onOutsideClick={closeLogoutConfirmation}
        >
          <LogoutConfirmation onClose={closeLogoutConfirmation} />
        </PortalPopup>
      )}
    </>
  );
};

export default CDI_1;
